/**
 * Loop guard for the automatic web reloads that move a tab onto the deployed build (AUTHZ-02 E2,
 * decision 3): at most ONE automatic reload per target build per window, remembered in
 * sessionStorage so it survives the reload it is guarding.
 *
 * Why a guard is needed at all: the tab now knows its own build (`__BUILD_ID__`), so a reload
 * that does not land on the target — an edge or browser cache still serving the old shell —
 * leaves a tab that still disagrees with `version.json`, and every startup or route change
 * would reload it again. One attempt per target per window turns that loop into one reload and
 * a persistent update banner.
 *
 * Every automatic reload is recorded BEFORE it navigates, and the next page load checks whether
 * it landed (`verifyReloadOutcome`). Missed landings are counted per target, separately from
 * attempts, so a deploy whose shell never arrives is visible as such and not as "reloaded".
 *
 * A reload that cannot be recorded is not attempted (`recordReloadAttempt` returns false):
 * without the record the next load cannot know it already tried, which is exactly the loop.
 * Manual reloads (a button the user pressed) do not go through this guard.
 */

export const RELOAD_GUARD_STORAGE_KEY = 'buildlog.buildReloadGuard'
export const RELOAD_GUARD_WINDOW_MS = 10 * 60 * 1000

type PendingReload = { target: string; at: number }

type GuardState = {
  /** The automatic reload this tab started and has not verified yet. */
  pending: PendingReload | null
  /** When the last automatic reload towards each target started. */
  attemptedAt: Record<string, number>
  /** How many automatic reloads towards each target did not land on it. */
  missed: Record<string, number>
}

export type ReloadOutcome = {
  target: string
  landed: boolean
  /** Missed landings for this target in this tab, including this one. */
  missedCount: number
}

function emptyState(): GuardState {
  return { pending: null, attemptedAt: {}, missed: {} }
}

function numberRecord(value: unknown): Record<string, number> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return {}
  const out: Record<string, number> = {}
  for (const [key, entry] of Object.entries(value as Record<string, unknown>)) {
    if (typeof entry === 'number' && Number.isFinite(entry) && entry >= 0) out[key] = entry
  }
  return out
}

function parsePending(value: unknown): PendingReload | null {
  if (typeof value !== 'object' || value === null) return null
  const { target, at } = value as Partial<PendingReload>
  if (typeof target !== 'string' || target === '') return null
  if (typeof at !== 'number' || !Number.isFinite(at)) return null
  return { target, at }
}

/** `null` when sessionStorage cannot be read at all — callers treat that as "do not reload". */
function readState(): GuardState | null {
  try {
    const raw = sessionStorage.getItem(RELOAD_GUARD_STORAGE_KEY)
    if (raw === null) return emptyState()
    const parsed: unknown = JSON.parse(raw)
    if (typeof parsed !== 'object' || parsed === null) return emptyState()
    const state = parsed as Partial<Record<keyof GuardState, unknown>>
    return {
      pending: parsePending(state.pending),
      attemptedAt: numberRecord(state.attemptedAt),
      missed: numberRecord(state.missed),
    }
  } catch (error) {
    // A corrupt value is replaced on the next write; unreadable storage is reported as such.
    if (error instanceof SyntaxError) return emptyState()
    return null
  }
}

function writeState(state: GuardState): boolean {
  try {
    sessionStorage.setItem(RELOAD_GUARD_STORAGE_KEY, JSON.stringify(state))
    return true
  } catch {
    return false
  }
}

/** Whether an automatic reload towards `target` is allowed now. */
export function canAttemptReload(target: string, now: number = Date.now()): boolean {
  const state = readState()
  if (state === null) return false
  const last = state.attemptedAt[target]
  if (last === undefined) return true
  // A timestamp from the future (clock moved back) must not block the target forever.
  return now - last >= RELOAD_GUARD_WINDOW_MS || last > now
}

/**
 * Record an automatic reload towards `target`, just before navigating. Returns false — and the
 * caller must NOT reload — when the record could not be written.
 */
export function recordReloadAttempt(target: string, now: number = Date.now()): boolean {
  const state = readState()
  if (state === null) return false
  return writeState({
    ...state,
    pending: { target, at: now },
    attemptedAt: { ...state.attemptedAt, [target]: now },
  })
}

/**
 * Called once per page load with the build this page actually runs. Resolves the reload the
 * previous page started, if any: did it land on its target?
 */
export function verifyReloadOutcome(currentBuild: string): ReloadOutcome | null {
  const state = readState()
  if (state === null || state.pending === null) return null

  const { target } = state.pending
  const landed = currentBuild === target
  const missed = landed
    ? state.missed
    : { ...state.missed, [target]: (state.missed[target] ?? 0) + 1 }
  writeState({ ...state, pending: null, missed })

  return { target, landed, missedCount: missed[target] ?? 0 }
}

/** Missed landings towards `target` in this tab. */
export function missedReloadCount(target: string): number {
  return readState()?.missed[target] ?? 0
}
